<template>
  <div class="help-center">
    <Search @search="onSearch" />
    <div class="faq">
      <div class="faq_title">常见问题</div>
      <div v-if="helpCenterData" class="faq_list">
        <div class="faq-category" v-for="(category, index) in helpCenterData.faqCategories" :key="index">
          <div class="faq-category__title">{{ category.title }}</div>
          <div class="faq-category__content">
            <ul :style="{
              height: category.showMoreStatus ? '180px' : `${category.faqs.length * 36}px`,
              overflowY: category.showMoreStatus ? 'hidden' : 'auto'
            }">
              <li v-for="(faq, faqIndex) in category.faqs" :key="faqIndex">
                <nuxt-link :to="`/help-center/faq/${faq.id}`">{{ faq.attributes.title }}</nuxt-link>
              </li>
            </ul>
            <div v-if="category.faqs.length > 5" class="show-more"
              @click="category.showMoreStatus = !category.showMoreStatus">
              {{ category.showMoreStatus ? '查看更多' : '收起更多' }}
              <icon-down v-if="category.showMoreStatus" style="color: #386AFF; font-size: 16px;" />
              <icon-up v-else />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="quick-faq">
      <div class="quick-faq__title">快问快答</div>
      <a-collapse :default-active-key="['0']" expand-icon-position="right">
        <template #expand-icon="{ active }">
          <icon-up v-if="active" style="color: #86909C;" />
          <icon-down v-else style="color: #86909C;" />
        </template>
        <a-collapse-item v-for="(item, index) in helpCenter.quickFaqContent" :key="`${index}`">
          <template #header>
            <div class="quick-faq__header">
              <div class="quick-faq__header-icon">问</div>{{ item.question }}
            </div>
          </template>

          <div class="answer">{{ item.answer }}</div>
        </a-collapse-item>
      </a-collapse>
    </div>
    <div class="free-question">
      <div class="question-title">“ 我们有没有漏掉什么？还有问题吗？”</div>
      <div class="question-desc">DG.hire海外雇佣资深专家，免费提供1v1咨询,专属学习资料，最新政策、资讯限时免费发送</div>
      <a-button class="free-question-btn" type="primary" @click="useLeadsInfo('帮助页面-免费提问', 'contactUs')">
        免费提问
      </a-button>
    </div>
    <div class="practical-tool">
      <div class="practical-tool-title">实用工具</div>
      <div class="practical-tool-desc">从本地薪酬洞察到每个国家的雇主成本，我们广泛收集的全球招聘资源可以回答您所有的国际招聘问题。</div>
      <div class="tool-list">
        <div class="tool-item" v-for="tool in helpCenter.toolList" :key="tool.title"
          :style="{ backgroundImage: `url(${tool.bgUrl})` }">
          <div class="tool-title">{{ tool.title }}</div>
          <div class="tool-desc">{{ tool.desc }}</div>
          <a-button class="tool-btn" type="outline" @click="router.push(tool.path)">
            了解更多
            <icon-right style="font-size: 30px; color: #2656FF;" />
          </a-button>
        </div>
      </div>
    </div>
    <div v-if="helpCenterData?.glossaryList" class="glossary">
      <div class="title">海外雇佣术语表</div>
      <div class="glossary-list">
        <div class="glossary-item" v-for="item in helpCenterData.glossaryList" :key="item.id">
          <nuxt-link :to="`/help-center/faq/${item.id}`">{{ item.attributes.title }}</nuxt-link>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { IListQuery, IList, IEntity } from '@/@types/cms';
import { IFaq } from '@/@types/entities';
import Search from './components/search.vue';
import helpCenter from '~~/assets/contents/help-center.json';
import useLeadsInfo from '@/composables/use-leads-info';
import { IconRight } from '@arco-design/web-vue/es/icon';
useSeoHead();

const router = useRouter();

type Category = { name: string; title: string; faqs: IEntity<IFaq>[], showMoreStatus?: boolean };
const { data: helpCenterData } = await useAsyncData(async () => {
  const [{ data: question }, { data: glossary }] = await Promise.all([
    useFetch('/api/faq/list?type=question'),
    useFetch('/api/faq/list?type=glossary')
  ]);
  const faqCategories = question.value?.data.reduce((result: Category[], item: IEntity<IFaq>) => {
    item.attributes.knowledges.data?.forEach(knowledge => {
      const { name, title } = knowledge.attributes;
      let category = result.find(res => res.name === name);
      if (!category) {
        category = {
          name,
          title,
          faqs: [],
          showMoreStatus: true
        };
        result.push(category);
      }
      category.faqs.push(item);
    });
    return result;
  }, [] as Category[]);
  const glossaryList = glossary.value.data.map((g: IEntity<IFaq>) => g);
  return {
    faqCategories,
    glossaryList
  };
});

async function onSearch(keyword: string) {
  if (!keyword) {
    return;
  }
  await navigateTo({
    path: '/help-center/search',
    query: {
      keyword,
    },
  });
}

</script>
<style lang="less" scoped>
.faq {
  background: linear-gradient(180deg, #F2F5FF 0%, #F2F5FF 0.01%, rgba(249, 250, 255, 0.5) 100%);
  padding: 60px 0 80px;

  &_title {
    font-weight: 500;
    font-size: 32px;
    line-height: 48px;
    color: #000000;
    text-align: center;
  }

  &_list {
    margin-top: 40px;
    display: flex;
    gap: 28px;
    justify-content: center;
    height: 368px;

    .faq-category {
      width: 408px;
      height: 368px;
      background: #FFFFFF;
      border-radius: 16px;
      position: relative;
      z-index: 9;

      &__title {
        height: 88px;
        font-weight: 500;
        font-size: 28px;
        line-height: 39px;
        text-align: center;
        color: #1D2129;
        box-sizing: border-box;
        padding: 24px 0;
        border-radius: 16px 16px 0 0;
      }

      &:nth-child(1) {
        .faq-category__title {
          background: url('https://video.reta-inc.com/image/default/6A71B50EF2294011816755525DA73B77-6-2.jpg') no-repeat center center/cover;
        }
      }

      &:nth-child(2) {
        .faq-category__title {
          background: url('https://video.reta-inc.com/image/default/75C44A268E954F958E79BE9CAFE599BB-6-2.jpg') no-repeat center center/cover;
        }
      }

      &:nth-child(3) {
        .faq-category__title {
          background: url('https://video.reta-inc.com/image/default/4DCD4693F2F541E884604D4FD8E20E3A-6-2.jpg') no-repeat center center/cover;
        }
      }

      &__content {
        font-size: 16px;
        line-height: 36px;
        letter-spacing: 0.02em;
        padding: 32px 24px 68px;
        background: #FFFFFF;
        position: relative;
        border-radius: 0 0 16px 16px;
        box-shadow: 0px 4px 24px rgba(114, 148, 226, 0.2);

        ul {
          max-height: 500px;
          transition: all .5s;
        }

        ul,
        li {
          margin: 0;
          padding: 0;
          list-style-position: inside;
        }

        a {
          color: #4E5969;
          text-decoration: none;

          &:hover {
            color: #386AFF;
          }
        }

        .show-more {
          font-weight: 400;
          font-size: 14px;
          line-height: 20px;
          letter-spacing: 0.01em;
          color: #386AFF;
          text-align: center;
          position: absolute;
          left: 0;
          right: 0;
          bottom: 24px;
          margin: auto;
          cursor: pointer;
        }
      }
    }
  }
}

.quick-faq {
  width: 1280px;
  margin: 0 auto;

  &__title {
    font-weight: 500;
    font-size: 32px;
    line-height: 48px;
    color: 000;
    text-align: center;
  }

  .answer {
    white-space: pre-wrap;
  }

  :deep(.arco-collapse) {
    margin-top: 36px;
    border: 0;
    overflow: visible;

    .arco-collapse-item {
      background: #FFFFFF;
      box-shadow: 0px 2px 24px rgba(114, 148, 226, 0.14);
      border: 0;
      border-radius: 12px;

      .arco-collapse-item-header {
        border: 0;
        border-radius: 12px;
        font-weight: 500;
        font-size: 18px;
        line-height: 24px;
        padding: 20px 32px;

        .arco-collapse-item-icon-right {
          right: 32px;
        }
      }

      .arco-collapse-item-content {
        background: none;
        border-top: 1px solid #E5E7EB;
        font-size: 16px;
        line-height: 28px;
        color: #4E5969;
        padding: 0 32px;

        .arco-collapse-item-content-box {
          padding: 20px 0 44px;
        }
      }

      &-active {
        .arco-collapse-item-header {
          border-bottom-right-radius: 0;
          border-bottom-left-radius: 0;
        }
      }

      & + .arco-collapse-item {
        margin-top: 20px;
      }
    }
  }

  &__header {
    display: flex;
    align-items: center;

    .quick-faq__header-icon {
      width: 22px;
      height: 22px;
      background: linear-gradient(180deg, #FFC47E 0%, #FE9D2B 100%);
      box-shadow: 0px 2px 4px rgba(190, 103, 0, 0.16);
      border-radius: 4px;
      margin-right: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 500;
      font-size: 12px;
      line-height: 14px;
      color: #FFFFFF;
    }
  }
}

.free-question {
  width: 100%;
  height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 60px;
  background: linear-gradient(0deg, rgba(95, 38, 255, 0.12), rgba(95, 38, 255, 0.12)), url('https://video.reta-inc.com/image/default/F81AC01FF7444745A97F6F8E2BB410FD-6-2.jpg') no-repeat center center/1920px 180px;

  .question-title {
    font-weight: 700;
    font-size: 28px;
    line-height: 48px;
    color: #FFFFFF;
    margin-top: 24px;
  }

  .question-desc {
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: #FFFFFF;
    margin-top: 4px;
  }

  .free-question-btn {
    width: 144px;
    height: 48px;
    margin-top: 16px;
    background: #3C5BFF;
    box-shadow: 0px 7px 8px rgba(49, 106, 255, 0.2);
    border-radius: 6px;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    color: #FFFFFF;
  }
}

.practical-tool {
  width: 100%;
  padding: 60px 0 80px;
  box-sizing: border-box;
  background: #F6F8FF;

  .practical-tool-title,
  .practical-tool-desc {
    text-align: center;
  }

  .practical-tool-title {
    font-weight: 500;
    font-size: 32px;
    line-height: 48px;
    letter-spacing: 0.5px;
    color: #000000;
  }

  .practical-tool-desc {
    font-weight: 400;
    font-size: 16px;
    line-height: 22px;
    color: #86909C;
    margin-top: 12px;
  }

  .tool-list {
    width: 1280px;
    margin: 40px auto 0;
    display: flex;
    justify-content: space-between;

    .tool-item {
      flex-shrink: 0;
      width: 408px;
      height: 340px;
      background: #fff no-repeat top center/408px 120px;
      box-shadow: 0px 4px 24px rgba(114, 148, 226, 0.2);
      border-radius: 16px;
      position: relative;

      .tool-title {
        font-weight: 500;
        font-size: 32px;
        line-height: 120px;
        text-align: center;
        letter-spacing: 0.02em;
        color: #FFFFFF;
      }

      .tool-desc {
        font-weight: 400;
        font-size: 16px;
        line-height: 28px;
        color: #4E5969;
        padding: 28px;
        box-sizing: border-box;
      }

      .tool-btn {
        width: 140px;
        height: 42px;
        font-weight: 500;
        font-size: 18px;
        color: #2656FF;
        border: 2px solid #2656FF;
        filter: drop-shadow(0px 7px 8px rgba(89, 135, 255, 0.2));
        border-radius: 6px;
        position: absolute;
        bottom: 32px;
        left: 0;
        right: 0;
        margin: auto;
      }
    }
  }
}

.glossary {
  margin-bottom: 80px;

  .title {
    font-weight: 500;
    font-size: 32px;
    line-height: 48px;
    text-align: center;
    letter-spacing: 0.5px;
    color: #000000;
    margin-top: 40px;
  }

  .glossary-list {
    width: 1280px;
    margin: 34px auto 0;
    background: url('https://video.reta-inc.com/image/default/F4703FDC0FA44DBBA5132E850BC29CF2-6-2.jpg') no-repeat center center/cover;
    padding: 48px;
    box-sizing: border-box;
    border-radius: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;

    .glossary-item {
      padding: 11px 24px;
      font-weight: 400;
      font-size: 16px;
      line-height: 22px;
      background: #FFFFFF;
      box-shadow: 0px 1px 8px rgba(114, 148, 226, 0.2);
      border-radius: 4px;
      cursor: pointer;
      user-select: none;

      a {
        color: #1D2129;
        text-decoration: none;
      }

      &:hover {
        a {
          color: #165DFF;
        }
      }
    }
  }
}

@media screen and (max-width: 800px) {
  .faq {
    padding: 20px 20px 40px 20px;

    &_title {
      font-size: 18px;
      line-height: 25px;
    }

    &_list {
      display: flex;
      height: auto;
      flex-direction: column;
      margin-top: 16px;

      .faq-category {
        width: auto;
        height: auto;

        &__title {
          height: auto;
          padding: 13px 0;
          font-size: 16px;
          line-height: 22px;
        }
        &__content {
          font-size: 14px;
          line-height: 28px;
          padding: 16px 20px 45px;
        }
      }
    }
  }

  .quick-faq {
    width: auto;
    margin-top: 20px;
    padding: 0 20px;

    &__title {
      font-size: 18px;
      line-height: 25px;
      text-align: center;
    }

    .quick-faq__header-icon {
      width: 20px;
      height: 20px;
    }

    .quick-faq__header {
      font-size: 14px;
      line-height: 24px;
      padding-right: 24px;
    }

    :deep(.arco-collapse-item-content-box) {
      padding: 16px 0 !important;
    }

    .answer {
      font-weight: 400;
      font-size: 12px;
      line-height: 24px;
    }
  }

  .free-question {
    height: 135px;

    .question-title {
      font-size: 19px;
      line-height: 26px;
    }

    .question-desc {
      font-size: 11px;
      line-height: 15px;
      text-align: center;
      width: 280px;
    }

    .free-question-btn {
      width: 88px;
      height: 28px;
      font-size: 14px;
      line-height: 24px;
      margin-top: 12px;
    }
  }

  .practical-tool {
    padding: 32px 20px;

    .practical-tool-desc {
      font-size: 12px;
      line-height: 20px;
    }

    .practical-tool-title {
      font-size: 18px;
      line-height: 25px;
    }

    .tool-list {
      gap: 24px;
      width: auto;
      flex-direction: column;
      margin: 16px;

      .tool-item {
        width: auto;
        height: 250px;
        background: #fff no-repeat top center / 100% 66px;

        .tool-title {
          padding: 16px 0;
          font-size: 22px;
          line-height: 31px;
        }

        .tool-desc {
          font-size: 14px;
          line-height: 24px;
        }

        .tool-btn {
          width: 100px;
          height: 32px;
          font-size: 14px;
          line-height: 24px;
          border: 1px solid #2656FF;
        }
      }
    }
  }

  .glossary {
    .title {
      font-size: 18px;
      line-height: 25px;
    }

    .glossary-list {
      width: auto;
      margin: 12px 20px;

      .glossary-item {
        padding: 5px 8px;
        font-size: 12px;
      }
    }
  }
}
</style>
